import { useState, useEffect } from 'react';
import { Phone, Sparkles, MapPin, Mail, Instagram, Facebook, ArrowUp, Home, User, Layers, FolderOpen } from 'lucide-react';

const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/placof', color: 'from-blue-500 to-blue-600' },
    { icon: Instagram, href: 'https://www.instagram.com/placof/', color: 'from-pink-500 to-purple-600' },
    { icon: 'tiktok', href: 'https://www.tiktok.com/@amir_decor', color: 'from-black to-gray-800' }
  ];

  // Utilisation des icônes Lucide
  const HomeIcon = Home;
  const AboutIcon = User;
  const ServicesIcon = Layers;
  const ProjectsIcon = FolderOpen;
  const ContactIcon = Mail;

  // Mettre à jour votre tableau
  const quickLinks = [
    { id: 'accueil', label: 'Accueil', href: '/', icon: <HomeIcon className="w-5 h-5" /> },
    { id: 'apropos', label: 'À Propos', href: '/about', icon: <AboutIcon className="w-5 h-5" /> },
    { id: 'services', label: 'Services', href: '/services', icon: <ServicesIcon className="w-5 h-5" /> },
    { id: 'projets', label: 'Projets', href: '/projects', icon: <ProjectsIcon className="w-5 h-5" /> },
    { id: 'contact', label: 'Contact', href: '/contact', icon: <ContactIcon className="w-5 h-5" /> },
  ];

  const services = [
    { label: 'Décoration Intérieure', icon: '🛋️' },
    { label: 'Aménagement Extérieur', icon: '🍃' },
    { label: 'Design Commercial', icon: '🏢' },
    { label: 'Consultation en Décoration', icon: '💡' }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white pt-20 pb-8 overflow-hidden">
      {/* Effet de particules de fond */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-20 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.5}s`,
              transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.005}px)`
            }}
          />
        ))}
      </div>

      {/* Vagues décoratives */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-transparent to-gray-900/50">
        <svg viewBox="0 0 1200 120" className="w-full h-full">
          <path d="M0,0V7.23C0,65.52,268.63,112.77,600,112.77S1200,65.52,1200,7.23V0Z" className="fill-amber-500/10"></path>
          <path d="M0,0V15.81C0,72.92,268.63,118.27,600,118.27S1200,72.92,1200,15.81V0Z" className="fill-orange-500/10"></path>
        </svg>
      </div>

      <div className="container mx-auto px-5 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Section À propos avec animation */}
          <div className="space-y-6 group">
            <div className="relative">
              <h3 className="text-2xl font-bold pb-4 relative overflow-hidden">
                <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                  Placof Décor
                </span>
                <div className="absolute bottom-0 left-0 w-20 h-1 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </h3>
              <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-amber-400 animate-pulse opacity-60" />
            </div>

            <p className="text-gray-300 leading-relaxed group-hover:text-white transition-colors duration-300">
              Votre partenaire premium en décoration intérieure et extérieure pour des espaces élégants, innovants et fonctionnels qui reflètent votre personnalité unique.
            </p>

            {/* Réseaux sociaux avec animations */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${social.color} text-white flex items-center justify-center text-lg hover:scale-110 hover:-rotate-12 hover:shadow-2xl transition-all duration-500 group relative overflow-hidden`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  {social.icon === 'tiktok' ? (
                    <svg className="w-5 h-5 relative z-10 group-hover:animate-bounce" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                    </svg>
                  ) : (
                    <social.icon className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* Liens rapides */}
          <div className="space-y-6 group">
            <h4 className="text-xl font-bold pb-4 relative">
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Liens Rapides
              </span>
              <div className="absolute bottom-0 left-0 w-16 h-1 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </h4>
            <ul className="space-y-4">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-amber-400 transition-all duration-300 flex items-center gap-3 group/item hover:translate-x-2"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <span className="group-hover/item:animate-bounce">{link.icon}</span>
                    <span className="relative">
                      {link.label}
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-400 to-orange-500 group-hover/item:w-full transition-all duration-300" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6 group">
            <h4 className="text-xl font-bold pb-4 relative">
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Nos Services
              </span>
              <div className="absolute bottom-0 left-0 w-16 h-1 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </h4>
            <ul className="space-y-4">
              {services.map((service, index) => (
                <li key={index}>
                  <button
                    className="text-gray-300 hover:text-amber-400 transition-all duration-300 flex items-center gap-3 group/item hover:translate-x-2"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <span className="text-lg group-hover/item:animate-bounce">{service.icon}</span>
                    <span className="relative">
                      {service.label}
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-400 to-orange-500 group-hover/item:w-full transition-all duration-300" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact avec animations */}
          <div className="space-y-6 group">
            <h4 className="text-xl font-bold pb-4 relative">
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Contactez-nous
              </span>
              <div className="absolute bottom-0 left-0 w-16 h-1 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </h4>
            <ul className="space-y-6">
              <li className="flex items-center gap-4 group/contact hover:translate-x-2 transition-transform duration-300">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 group-hover/contact:scale-110 transition-transform duration-300">
                  <MapPin className="w-5 h-5 text-white group-hover/contact:animate-bounce" />
                </div>
                <span className="text-gray-300 group-hover/contact:text-white transition-colors duration-300">
                  Sfax, Tunis
                </span>
              </li>
              <li className="flex items-center gap-4 group/contact hover:translate-x-2 transition-transform duration-300">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 group-hover/contact:scale-110 transition-transform duration-300">
                  <Phone className="w-5 h-5 text-white group-hover/contact:animate-bounce" />
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-300 group-hover/contact:text-white transition-colors duration-300 font-semibold">
                    +216 97 286 007
                  </span>
                </div>
              </li>
              <li className="flex items-center gap-4 group/contact hover:translate-x-2 transition-transform duration-300">
                <a
                  href="mailto:placof.decor@gmail.com"
                  className="flex items-center gap-4 w-full"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 group-hover/contact:scale-110 transition-transform duration-300">
                    <Mail className="w-5 h-5 text-white group-hover/contact:animate-bounce" />
                  </div>
                  <span className="text-gray-300 group-hover/contact:text-white transition-colors duration-300">
                    placof.decor@gmail.com
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Section copyright avec design premium */}
        <div className="pt-8 border-t border-gradient-to-r from-amber-500/20 via-orange-500/20 to-transparent">
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent font-semibold">Placof Décor</span>. Tous droits réservés.
            </p>
            <div className="text-sm text-gray-400">
              <span>Développé par  </span>
              <a
                href="https://www.linkedin.com/in/mohamed-ali-zid-b64b70253/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 text-transparent bg-clip-text font-semibold hover:brightness-110 transition duration-300"
              >
                Mohamed Ali ZID
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bouton scroll to top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl shadow-2xl hover:shadow-amber-500/25 transition-all duration-500 z-50 group overflow-hidden hover:scale-110 hover:-rotate-12 flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
          <ArrowUp className="w-6 h-6 relative z-10 group-hover:animate-bounce" />
        </button>
      )}
    </footer>
  );
};

export default Footer;